/**
 * Input History Store
 *
 * Tracks transcriptions and pastes per project so the user can
 * review and re-insert them if the original write to the terminal
 * failed or was lost.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface InputHistoryEntry {
  id: string;
  text: string;
  /**
   * Where this entry came from:
   *  - 'transcription' = local Whisper raw transcription (mic button)
   *  - 'paste'         = clipboard / drag-drop / history re-paste
   *  - 'prompt'        = AI-enhanced transcription (raw transcript cleaned up
   *                      by the local AI; already a ready-to-send prompt, do
   *                      not re-wrap or re-process)
   */
  source: 'transcription' | 'paste' | 'prompt';
  /**
   * For AI-enhanced entries (`source: 'prompt'`), the ORIGINAL raw transcript
   * the enhancement was derived from. Lets the history panel offer a per-entry
   * "Enhanced ⇄ Raw" toggle so the user can compare the two and copy/insert
   * whichever actually matches what they meant. Absent for entries with no
   * distinct raw origin (plain transcriptions, pastes).
   */
  rawText?: string;
  timestamp: number;
  projectPath: string;
}

interface InputHistoryState {
  entries: InputHistoryEntry[];
  /** Add a new entry. Keeps the most recent 50 per project. Returns the new
   *  entry's id so callers can later augment/replace it (e.g. swap a raw
   *  transcription for its paired AI-enhanced version once enhancement lands). */
  addEntry: (
    text: string,
    source: InputHistoryEntry['source'],
    projectPath: string,
    rawText?: string,
  ) => string;
  /** Get entries for a specific project, newest first. */
  getEntries: (projectPath: string) => InputHistoryEntry[];
  /** Remove a single entry by ID. */
  removeEntry: (id: string) => void;
  /** Clear all entries for a project. */
  clearProject: (projectPath: string) => void;
}

const MAX_ENTRIES_PER_PROJECT = 50;

export const useInputHistoryStore = create<InputHistoryState>()(
  persist(
    (set, get) => ({
      entries: [],

      addEntry: (text, source, projectPath, rawText) => {
        const trimmed = text.trim();
        if (!trimmed) return '';
        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const trimmedRaw = rawText?.trim();
        const entry: InputHistoryEntry = {
          id,
          text: trimmed,
          source,
          // Only attach a raw origin when it's actually a distinct string —
          // no point offering a "Raw" toggle that shows the same text.
          ...(trimmedRaw && trimmedRaw !== trimmed ? { rawText: trimmedRaw } : {}),
          timestamp: Date.now(),
          projectPath,
        };
        set((state) => {
          const updated = [entry, ...state.entries];
          // Keep only MAX per project
          const projectCount = new Map<string, number>();
          return {
            entries: updated.filter((e) => {
              const count = (projectCount.get(e.projectPath) ?? 0) + 1;
              projectCount.set(e.projectPath, count);
              return count <= MAX_ENTRIES_PER_PROJECT;
            }),
          };
        });
        return id;
      },

      getEntries: (projectPath) => {
        return get().entries.filter((e) => e.projectPath === projectPath);
      },

      removeEntry: (id) => {
        set((state) => ({
          entries: state.entries.filter((e) => e.id !== id),
        }));
      },

      clearProject: (projectPath) => {
        set((state) => ({
          entries: state.entries.filter((e) => e.projectPath !== projectPath),
        }));
      },
    }),
    {
      name: 'ateliercode-input-history',
    }
  )
);
